(() => {
  if (window.BloomAccessibility) return;

  const STORAGE_KEY = 'bloom-accessibility-preferences-v1';
  const DEFAULT_STATE = {
    color: 'none',
    font: 'default',
    links: false,
    headings: false,
    motion: false,
  };

  const widgets = document.querySelectorAll('[data-bloom-accessibility]');
  if (!widgets.length) return;

  const normalizeState = (value = {}) => {
    const allowedColors = ['none', 'contrast', 'invert', 'mono'];
    const allowedFonts = ['small', 'default', 'large', 'larger'];

    return {
      color: allowedColors.includes(value.color) ? value.color : DEFAULT_STATE.color,
      font: allowedFonts.includes(value.font) ? value.font : DEFAULT_STATE.font,
      links: value.links === true,
      headings: value.headings === true,
      motion: value.motion === true,
    };
  };

  const readState = () => {
    try {
      return normalizeState(JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'));
    } catch (_error) {
      return { ...DEFAULT_STATE };
    }
  };

  let state = readState();

  const persist = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_error) {
      // Preferences still work for the current page when storage is unavailable.
    }
  };

  const setRootAttribute = (name, value, inactiveValue) => {
    const root = document.documentElement;
    if (value === inactiveValue) root.removeAttribute(name);
    else root.setAttribute(name, value);
  };

  const applyState = () => {
    setRootAttribute('data-bloom-a11y-color', state.color, 'none');
    setRootAttribute('data-bloom-a11y-font', state.font, 'default');
    setRootAttribute('data-bloom-a11y-links', state.links ? 'true' : 'false', 'false');
    setRootAttribute('data-bloom-a11y-headings', state.headings ? 'true' : 'false', 'false');
    setRootAttribute('data-bloom-a11y-motion', state.motion ? 'reduced' : 'default', 'default');

    widgets.forEach((widget) => {
      widget.querySelectorAll('[data-setting]').forEach((button) => {
        const setting = button.dataset.setting;
        const value = button.dataset.value;
        const active = setting === 'color'
          ? state.color === value
          : Boolean(state[setting]);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    });
  };

  const announce = (widget, message) => {
    const status = widget.querySelector('[data-a11y-status]');
    if (!status) return;
    status.textContent = '';
    window.setTimeout(() => {
      status.textContent = message;
    }, 20);
  };

  const updateFont = (direction) => {
    const steps = ['small', 'default', 'large', 'larger'];
    const currentIndex = Math.max(0, steps.indexOf(state.font));
    const nextIndex = Math.min(steps.length - 1, Math.max(0, currentIndex + direction));
    const changed = nextIndex !== currentIndex;
    state.font = steps[nextIndex];
    return changed;
  };

  widgets.forEach((widget) => {
    const trigger = widget.querySelector('[data-a11y-open]');
    const dialog = widget.querySelector('[data-a11y-dialog]');
    const close = widget.querySelector('[data-a11y-close]');
    if (!trigger || !dialog || !close) return;

    const restoreTrigger = () => {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    };

    const handleFallbackKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDialog();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...dialog.querySelectorAll('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.disabled && element.getAttribute('aria-hidden') !== 'true');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const openDialog = () => {
      trigger.setAttribute('aria-expanded', 'true');
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else {
        dialog.setAttribute('open', '');
        document.addEventListener('keydown', handleFallbackKeydown);
      }
      close.focus();
    };

    const closeDialog = () => {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else {
        dialog.removeAttribute('open');
        document.removeEventListener('keydown', handleFallbackKeydown);
        restoreTrigger();
      }
    };

    trigger.addEventListener('click', openDialog);
    close.addEventListener('click', closeDialog);

    dialog.addEventListener('close', () => {
      restoreTrigger();
    });

    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      const clickedOutside = event.clientX < bounds.left
        || event.clientX > bounds.right
        || event.clientY < bounds.top
        || event.clientY > bounds.bottom;
      if (clickedOutside) closeDialog();
    });

    dialog.querySelectorAll('[data-action]').forEach((button) => {
      button.addEventListener('click', () => {
        const action = button.dataset.action;
        let message = 'העדפת הנגישות עודכנה';

        if (action === 'font-up') {
          message = updateFont(1) ? 'גודל הטקסט הוגדל' : 'הגעת לגודל הטקסט המרבי';
        } else if (action === 'font-down') {
          message = updateFont(-1) ? 'גודל הטקסט הוקטן' : 'הגעת לגודל הטקסט המזערי';
        } else if (action === 'font-reset') {
          state.font = 'default';
          message = 'גודל הטקסט חזר לברירת המחדל';
        } else if (action === 'reset') {
          state = { ...DEFAULT_STATE };
          message = 'כל העדפות הנגישות אופסו';
        } else if (button.dataset.setting === 'color') {
          const value = button.dataset.value;
          const wasActive = state.color === value;
          state.color = wasActive ? 'none' : value;
          const labels = {
            contrast: 'ניגודיות גבוהה',
            invert: 'ניגודיות הפוכה',
            mono: 'תצוגה מונוכרומטית',
          };
          message = wasActive ? `${labels[value]} בוטלה` : `${labels[value]} הופעלה`;
        } else if (button.dataset.setting) {
          const setting = button.dataset.setting;
          state[setting] = !state[setting];
        }

        applyState();
        persist();
        announce(widget, message);
      });
    });
  });

  applyState();
  window.BloomAccessibility = { reset: () => {
    state = { ...DEFAULT_STATE };
    applyState();
    persist();
  } };
})();
